import type { Question } from "./types"
import { SOLUTION_ITEMS } from "../constants"

export const ONLINE_SERVICE_LINKS = {
  gateway: "/za/gateway",
  payment_request: "/za/payment-link",
  invoices: "/za/invoicing",
  tap_to_pay: "/za/tap-to-pay-on-iphone",
}

// ── Questions (defined from leaves up to the root) ──────────────────────────

const barcodeQuestion: Question = {
  question: "Do you sell products with barcodes?",
  description:
    "Think retail items, packaged goods, or anything with a scannable label.",
  options: [
    {
      answer: "Yes",
      description: "I scan barcodes at the till",
      breadcrumb: "Barcode",
      solutionAdditions: [{ ...SOLUTION_ITEMS.barcode }],
    },
    {
      answer: "No",
      description: "I don't need barcode scanning",
    },
  ],
}

// Counter path: "Yes" adds khumoPrint; both options lead to barcode check
const counterMultiDeviceWithPrinterQuestion: Question = {
  question: "Will more than one person take payments at the same time?",
  description: "For multiple staff or busy periods.",
  options: [
    {
      answer: "Yes",
      description: "in multiple spots",
      breadcrumb: "Multi-device",
      solutionAdditions: [{ ...SOLUTION_ITEMS.khumoPrint }],
      nextQuestion: barcodeQuestion,
    },
    {
      answer: "No",
      description: "just one",
      breadcrumb: "Single device",
      nextQuestion: barcodeQuestion,
    },
  ],
}

// Counter path: "Yes" adds khumo; both options lead to barcode check
const counterMultiDeviceWithoutPrinterQuestion: Question = {
  question: "Will more than one person take payments at the same time?",
  description: "For multiple staff or busy periods.",
  options: [
    {
      answer: "Yes",
      description: "in multiple spots",
      breadcrumb: "Multi-device",
      solutionAdditions: [{ ...SOLUTION_ITEMS.khumo }],
      nextQuestion: barcodeQuestion,
    },
    {
      answer: "No",
      description: "just one",
      breadcrumb: "Single device",
      nextQuestion: barcodeQuestion,
    },
  ],
}

const paymentsQuestion: Question = {
  question: "Where do customers usually pay?",
  description: "Choose what best matches your setup.",
  options: [
    {
      answer: "At a fixed counter or till",
      description: "Customers come to you to pay",
      breadcrumb: "Fixed Counter",
      solutionAdditions: [{ ...SOLUTION_ITEMS.counter }],
      nextQuestion: {
        question: "Do you need to print receipts or order slips?",
        description: "Think printed receipts, kitchen orders, or both.",
        options: [
          {
            answer: "Yes",
            description: "Printed receipts or order slips",
            breadcrumb: "Printing",
            solutionAdditions: [{ ...SOLUTION_ITEMS.printer }],
            nextQuestion: counterMultiDeviceWithPrinterQuestion,
          },
          {
            answer: "No",
            description: "I'll send digital receipts",
            breadcrumb: "No Printing",
            nextQuestion: counterMultiDeviceWithoutPrinterQuestion,
          },
        ],
      },
    },
    {
      answer: "Around the space",
      description: "You take payments at tables or across your store.",
      breadcrumb: "Around the Space",
      nextQuestion: {
        question: "Do you need to print receipts or order slips?",
        description: "Think printed receipts, kitchen orders, or both.",
        options: [
          {
            answer: "Yes",
            breadcrumb: "Printing",
            description: "Printed receipts or order slips",
            solutionAdditions: [{ ...SOLUTION_ITEMS.khumoPrint }],
            nextQuestion: {
              question:
                "Will more than one person take payments at the same time?",
              description: "For multiple staff or busy periods.",
              options: [
                {
                  answer: "Yes",
                  description: "in multiple spots",
                  breadcrumb: "Multi-device",
                  solutionAdditions: [
                    {
                      ...SOLUTION_ITEMS.khumo,
                    },
                  ],
                },
                {
                  answer: "No",
                  description: "just one",
                  breadcrumb: "Single device",
                },
              ],
            },
          },
          {
            answer: "No",
            description: "I'll send digital receipts",
            breadcrumb: "No Printing",
            solutionAdditions: [
              { ...SOLUTION_ITEMS.khumo },
              { ...SOLUTION_ITEMS.tap_to_pay },
            ],
            nextQuestion: {
              question:
                "Will more than one person take payments at the same time?",
              description: "For multiple staff or busy periods.",
              options: [
                {
                  answer: "Yes",
                  description: "in multiple spots",
                  breadcrumb: "Multi-device",
                  solutionAdditions: [
                    {
                      ...SOLUTION_ITEMS.khumo,
                      superscript: "For additional staff",
                    },
                  ],
                },
                {
                  answer: "No",
                  description: "just one",
                  breadcrumb: "Single device",
                },
              ],
            },
          },
        ],
      },
    },
    {
      answer: "On the move",
      description: "You're mobile — markets, deliveries, or pop-ups.",
      breadcrumb: "On the Move",
      nextQuestion: {
        question: "Do you need to print receipts or order slips?",
        description: "Think printed receipts, kitchen orders, or both.",
        options: [
          {
            answer: "Yes",
            description: "Printed receipts or order slips",
            breadcrumb: "Printing",
            solutionAdditions: [{ ...SOLUTION_ITEMS.khumoPrint }],
          },
          {
            answer: "No",
            description: "I'll send digital receipts",
            breadcrumb: "No Printing",
            solutionAdditions: [
              { ...SOLUTION_ITEMS.khumo },
              { ...SOLUTION_ITEMS.tap_to_pay },
            ],
          },
        ],
      },
    },
  ],
}

// ── Entry point ─────────────────────────────────────────────────────────────

export const SOLUTION_FINDER_CONFIG: Question = {
  question: "How do you sell?",
  description: "Helps us recommend the right setup for your business.",
  options: [
    {
      answer: "In person",
      breadcrumb: "In-person",
      resultDescription:
        "Sell in person with a fully connected setup that keeps everything in sync.",
      nextQuestion: paymentsQuestion,
    },
    {
      answer: "Online",
      breadcrumb: "Online",
      resultDescription:
        "Accept payments online in the way that suits your business — no website required.",
      nextQuestion: {
        question: "How do you want to get paid online?",
        description: "Choose all that apply.",
        multipleAnswers: true,
        options: [
          {
            answer: "On your website or app",
            description: "Payment Gateway",
            breadcrumb: "Payment Gateway",
            solutionAdditions: [{ ...SOLUTION_ITEMS.gateway }],
          },
          {
            answer: "Share a payment link",
            description: "Payment Links",
            breadcrumb: "Payment Links",
            solutionAdditions: [{ ...SOLUTION_ITEMS.payment_request }],
          },
          {
            answer: "Send invoices to customers",
            description: "Invoices",
            breadcrumb: "Invoices",
            solutionAdditions: [{ ...SOLUTION_ITEMS.invoices }],
          },
        ],
      },
    },
    {
      answer: "In person & online",
      breadcrumb: "In-person + Online",
      resultDescription:
        "Sell in person and online with a fully connected setup that keeps everything in sync.",
      solutionAdditions: [{ ...SOLUTION_ITEMS.all_online }],
      nextQuestion: paymentsQuestion,
    },
  ],
}
